import { isFunction } from '@tanstack/react-table';

import { useControllableState } from '../../../../core/hooks/useControllableState.js';
import { isDev } from '../../../../core/utils/is-node-environment.js';
import { useDataTableV2ConfigElement } from '../../../hooks/useDataTableV2Config.js';
import { isGroupColumnDef } from '../../hooks/useTable/datatablev2-typeguards.js';
import {
  type Column,
  type ColumnDef,
  type Header,
  type Row,
  type RowData,
  type RowModel,
  type Table,
  type TableFeature,
  type TableOptions,
} from '../../hooks/useTable/types.js';
import type { DataTableV2RowData } from '../../public.api.js';
import {
  datelikeCompare,
  numberlikeCompare,
  stringlikeCompare,
  stringlikeCompareCaseSensitive,
} from '../Sorting/compare-functions.js';

/**
 * Extension interface to extend the types of the columnDefinition from the tanstack table.
 * @public
 */
export interface DataTableV2SortingColumnDef<
  TData extends DataTableV2RowData,
  TValue,
> {
  /**
   * Setting to true disables sorting for this column even
   * if sorting is enabled globally.
   */
  disableSorting?: boolean;

  /**
   * Used to compare two rows of data and order them correctly.
   * If a function is passed, it must be memoized.
   * @defaultValue 'text'
   */
  sortType?:
    | 'text'
    | 'number'
    | 'textCaseSensitive'
    | 'datetime'
    | DataTableV2CustomSortingFn<TValue>;

  /**
   * Setting to true means the first sorting direction for
   * this column will be descending instead of ascending.
   */
  sortDescFirst?: boolean;

  /**
   * Setting this to true means the underlying sorting
   * direction will be inverted but the UI will not. This
   * could be useful for example where a lower score is better.
   */
  sortInverted?: boolean;

  /**
   * Optional - to be used for providing the value used for sorting.
   * This enables the value used for sorting to be different than
   * the value returned by the accessor. For example, if the accessor
   * returns an object, the sortAccessor may return a number or string
   * field within the object.
   */
  sortAccessor?: string | ((row: TData) => TValue);
}

/**
 * Rule for sorting a column.
 * @public
 */
export interface DataTableV2ColumnSort {
  /**
   * Column ID.
   */
  id: string;
  /**
   * Descending sorting direction.
   */
  desc: boolean;
}

/**
 * @public
 */
export interface DataTableV2SortingBaseProps {
  /**
   * Enables sorting for all columns which do not have it explicitly
   * disabled with the `disableSorting` prop on the column entry.
   *
   * @defaultValue false
   */
  sortable?:
    | boolean
    | {
        /**
         * When set to true, the table will assume that the data provided by the consumer is already sorted,
         * and will not apply any sorting to it.
         */
        manualSort?: boolean;
      };
  /**
   * Callback triggered when sorting of the column is changed.
   */
  onSortByChange?: (columnSorting: DataTableV2ColumnSort[]) => void;
}

/**
 * @public
 */
export interface DataTableV2SortingControlledProps
  extends DataTableV2SortingBaseProps {
  /**
   * Controlled property to define the columns the DataTableV2 is sorted by.
   **/
  sortBy?: DataTableV2ColumnSort[];
  /**
   * Uncontrolled property to define the columns the DataTableV2 is sorted by.
   **/
  defaultSortBy?: never;
}

/**
 * @public
 */
export interface DataTableV2SortingUncontrolledProps
  extends DataTableV2SortingBaseProps {
  /**
   * Controlled property to define the columns the DataTableV2 is sorted by.
   **/
  sortBy?: never;
  /**
   * Uncontrolled property to define the columns the DataTableV2 is sorted by.
   **/
  defaultSortBy?: DataTableV2ColumnSort[];
}

/**
 * Options for DataTableV2 sorting
 * @public
 */
export type DataTableV2SortingProps =
  | DataTableV2SortingControlledProps
  | DataTableV2SortingUncontrolledProps;

/**
 * Extension interface to extend the tanstack table state with the sortable state.
 * @internal
 */
export interface DataTableV2SortingOptions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSortedRowModel?: (table: Table<any>) => () => RowModel<any>;
}

/**
 * Extension interface to extend the tanstack table header instance.
 * @internal
 */
export interface DataTableV2SortingHeader {
  getAriaSortAttribute: () =>
    | 'none'
    | 'ascending'
    | 'descending'
    | 'other'
    | undefined;

  getCanSortByHeaderClick: () => boolean;
}

/**
 * Extension interface to extend the types of the `Row` from the tanstack table.
 * @internal
 */
export interface DataTableV2SortingRow {
  _sortingValuesCache: Record<string, unknown>;

  getSortingValue: (columnId: string) => unknown;
}

/**
 * Compares two values from a given column for sorting.
 *
 * @param valueA - First value for comparison.
 * @param valueB - Second value for comparison.
 *
 * @returns the function is expected to return `-1`, `0`, or `1` in ascending order. `0` indicates that both values are equal,
 * `-1` indicates that `valueA` should come before `valueB`, `1` indicates that `valueA` should come after `valueB`.
 * @public
 */
export interface DataTableV2CustomSortingFn<TValue> {
  (valueA: TValue, valueB: TValue): number;
}

/**
 * Typeguard function that checks whether the given column definition has a 'sortAccessor' defined.
 */
function isColumnDefWithSortAccessor(
  columnDef: unknown,
): columnDef is ColumnDef<unknown> {
  return (
    columnDef !== undefined &&
    columnDef !== null &&
    typeof columnDef === 'object' &&
    'sortAccessor' in columnDef &&
    columnDef.sortAccessor !== undefined
  );
}

/**
 * Configuration hook for the DataTableV2 sorting feature.
 * @internal
 */
export function useSorting<TData extends DataTableV2RowData>(
  props: DataTableV2SortingProps,
  options: TableOptions<TData>,
) {
  const configSortable = useDataTableV2ConfigElement('sortable');

  const [sorting, setSorting] = useControllableState<DataTableV2ColumnSort[]>({
    value: props.sortBy,
    defaultValue: props.defaultSortBy,
    onChange: props.onSortByChange,
  });

  if (isDev() && sorting !== undefined && sorting.length > 1) {
    // eslint-disable-next-line no-console
    console.warn(
      'As for now, DataTableV2 does not offer support for sorting columns by more than one criterion.',
    );
  }

  const { sortable: sortableProp } = props;
  let sortable = false;

  if (sortableProp !== undefined) {
    if (typeof sortableProp === 'boolean') {
      sortable = sortableProp;
      options.manualSorting = false;
    } else {
      sortable = true;
      // To do: should reconsider is there a better and simpler way (simpler API exposed) that we can check wheater the consumer wants to keep default sorting
      // or they want to implement custom data pre sorting
      if (sortableProp.manualSort === true) {
        options.manualSorting = true;
      } else {
        options.manualSorting = undefined;
      }
    }
  } else if (configSortable !== undefined) {
    if (typeof configSortable === 'boolean') {
      sortable = configSortable;
    } else {
      sortable = true;

      if (configSortable.manualSort === true) {
        options.manualSorting = true;
      } else {
        options.manualSorting = undefined;
      }
    }
  }

  options.enableSorting = sortable;
  options.state = {
    ...options.state,
    sorting,
  };
  options.onSortingChange = setSorting;
}

const sortingFunctions = {
  text: stringlikeCompare,
  textCaseSensitive: stringlikeCompareCaseSensitive,
  number: numberlikeCompare,
  datetime: datelikeCompare,
};

/**
 * @internal
 */
export const Sorting: TableFeature = {
  createColumn<TData extends RowData, TValue>(
    column: Column<TData, TValue>,
    table: Table<TData>,
  ) {
    column.getCustomSortingFn = () => {
      if (!column) {
        throw new Error();
      }

      if (isFunction(column.columnDef.customSortingFn)) {
        return column.columnDef.customSortingFn;
      }

      if (
        typeof column.columnDef.sortType === 'string' &&
        sortingFunctions[column.columnDef.sortType]
      ) {
        return sortingFunctions[
          column.columnDef.sortType
        ] as DataTableV2CustomSortingFn<TValue>;
      }

      return sortingFunctions['text'] as DataTableV2CustomSortingFn<TValue>;
    };

    column.getAutoSortDir = () => {
      // overwrite the default from react table
      // by default all columns should be sorted ascending first
      return 'asc';
    };

    column.getCanSort = () => {
      // Grouped columns should for now not be sortable.
      if (isGroupColumnDef(column.columnDef)) {
        return false;
      }
      return (
        (column.columnDef.enableSorting ?? true) &&
        (table.options.enableSorting ?? false) &&
        !!column.accessorFn
      );
    };
  },

  createRow<TData extends RowData>(row: Row<TData>, table: Table<TData>) {
    row._sortingValuesCache = {};
    row.getSortingValue = (columnId: string) => {
      if (
        Object.prototype.hasOwnProperty.call(row._sortingValuesCache, columnId)
      ) {
        return row._sortingValuesCache[columnId];
      }

      const column = table.getColumn(columnId);

      if (column && isColumnDefWithSortAccessor(column.columnDef)) {
        const sortAccessor = column.columnDef.sortAccessor;
        if (sortAccessor && typeof sortAccessor === 'string') {
          return sortAccessor.split('.').reduce(
            // @ts-expect-error Strict mode transition - needs to be investigated
            (obj, key) => obj[key] as Record<string, unknown>,
            row.original,
          );
        } else if (typeof sortAccessor === 'function') {
          return sortAccessor(row.original);
        }
      }

      if (!column?.accessorFn) {
        return undefined;
      }

      row._sortingValuesCache[columnId] = column.accessorFn(
        row.original as TData,
        row.index,
      );

      return row._sortingValuesCache[columnId] as unknown;
    };
  },

  createHeader<TData extends RowData, TValue>(
    header: Header<TData, TValue>,
    table: Table<TData>,
  ) {
    header.getAriaSortAttribute = () => {
      if (
        table.options.enableSorting &&
        header.column.columnDef.enableSorting !== false &&
        header.column.columns.length === 0
      ) {
        if (header.column.getIsSorted()) {
          return header.column.getIsSorted() === 'asc'
            ? 'ascending'
            : 'descending';
        } else {
          return 'none';
        }
      } else {
        return undefined;
      }
    };

    header.getCanSortByHeaderClick = () => {
      if (header.column.getCanUserActions() === true) {
        return false;
      }
      return header.column.getCanSort();
    };
  },
};
